import React, { FC, useCallback, useEffect, useState } from 'react';
import { Button, Box, TextField, Autocomplete, MenuItem, ListItemText, Typography } from '@mui/material';
import parse from 'autosuggest-highlight/parse';
import match from 'autosuggest-highlight/match';
import { useLazyQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import { User } from 'models/user';
import { getDentistsForOrderTemplate } from 'apollo/templates/user/query.graphql';
import { currentOrgVar } from 'apollo/reactive-variables/organization';
import { debounce } from '@mui/material/utils';
import DHDialog from 'components/shared/dh-dialog/dh-dialog.component';

type SelectDentistDialogProps = {
    open: boolean;
    onDialogClose: () => void;
    onSelect: (dentist: User) => void;
};
const SelectDentistDialog: FC<SelectDentistDialogProps> = ({
    open,
    onDialogClose,
    onSelect,
}: SelectDentistDialogProps) => {
    const { t: translate } = useTranslation(['users', 'common']);
    const [dentists, setDentists] = useState<User[]>([]);
    const [dentist, setDentist] = useState<User>();
    const [inputValue, setInputValue] = useState('');
    const { showBoundary } = useDenthubErrorBoundary();
    const currentOrg = currentOrgVar();

    const [getDentists] = useLazyQuery(getDentistsForOrderTemplate, {
        onError: (err) => {
            showBoundary(err);
        },
        onCompleted: ({ dentists: searchedDentists }) => {
            let newOptions: User[] = [];
            if (dentist) {
                newOptions = [dentist];
            }
            if (searchedDentists) {
                newOptions = [...newOptions, ...searchedDentists];
            }
            setDentists(newOptions);
        },
    });

    const fetchDentists = useCallback(
        debounce((freeText: string) => {
            getDentists({
                variables: { clinicId: currentOrg._id, filters: { freeText: freeText, active: true }, limit: 15 },
            });
        }, 300),
        [],
    );

    useEffect(() => {
        if (inputValue.length < 2) {
            setDentists([]);
            return undefined;
        }
        fetchDentists(inputValue);
    }, [dentist, inputValue, fetchDentists]);

    const handleSelectClick = () => {
        if (dentist) {
            onSelect(dentist);
            setDentist(undefined);
            setDentists([]);
            setInputValue('');
        }
    };

    return (
        <DHDialog
            title={translate('orders:CHANGE_DENTIST')}
            fullWidth
            open={open}
            onClose={onDialogClose}
            actions={
                <Box>
                    <Button
                        type="submit"
                        variant="contained"
                        onClick={handleSelectClick}
                        disabled={!dentist}
                        data-test="select-dentist-dialog.select-btn"
                    >
                        {translate('common:SELECT')}
                    </Button>
                </Box>
            }
            data-test="select-dentist-dialog.dialog"
        >
            <form
                onSubmit={(e) => {
                    e.preventDefault();
                    handleSelectClick();
                }}
            >
                <Box mt={1}>
                    <Autocomplete
                        fullWidth
                        autoFocus
                        autoComplete
                        disableClearable
                        options={dentists}
                        getOptionLabel={(dentist) => dentist.fullName || ''}
                        filterOptions={(x) => x}
                        includeInputInList
                        filterSelectedOptions
                        noOptionsText={translate('orders:TYPE_TO_SEARCH')}
                        renderOption={(props, dentist) => {
                            const matches = match(dentist.fullName || '', inputValue);
                            const parts = parse(dentist.fullName || '', matches);
                            return (
                                <MenuItem
                                    {...props}
                                    key={dentist._id}
                                    value={dentist._id}
                                    data-test={`select-dentist-dialog.option-${dentist._id}`}
                                >
                                    <ListItemText>
                                        {parts.map((part, index) => {
                                            return (
                                                <Box
                                                    key={index}
                                                    component="span"
                                                    sx={{ fontWeight: part.highlight ? 'bold' : 'regular' }}
                                                >
                                                    {part.text}
                                                </Box>
                                            );
                                        })}
                                        <Typography variant="body2" color="grey.700">
                                            {dentist.email}
                                        </Typography>
                                        <Typography variant="body2" color="grey.700">
                                            {dentist.syncid}
                                        </Typography>
                                    </ListItemText>
                                </MenuItem>
                            );
                        }}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label={translate('users:SELECT_DENTIST')}
                                data-test="select-dentist-dialog.textfield"
                            />
                        )}
                        inputValue={inputValue}
                        value={dentist}
                        onChange={(_, dentist) => {
                            setDentist(dentist);
                        }}
                        onInputChange={(_, value) => {
                            setInputValue(value);
                        }}
                    />
                </Box>{' '}
            </form>
        </DHDialog>
    );
};

export default SelectDentistDialog;
